import React from 'react';
import { useAuth } from '@/state/auth/AuthProvider';

export interface AccessControlProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredRoles?: string[];
  allowedTenants?: string[];
  requireTenantMatch?: boolean;
  fallback?: React.ReactNode;
}

export const AccessControl: React.FC<AccessControlProps> = ({
  children,
  requiredRole,
  requiredRoles,
  allowedTenants,
  requireTenantMatch = false,
  fallback = null
}) => {
  const { user, isAuthenticated } = useAuth();
  const permissions = usePermissions();

  if (!isAuthenticated || !user) {
    return <>{fallback}</>;
  }

  // Check role-based access
  if (requiredRole) {
    // Special handling for PLATFORM_ADMIN - use isPlatformAdmin() which checks multiple role formats
    if (requiredRole === 'PLATFORM_ADMIN') {
      if (!permissions.isPlatformAdmin()) {
        return <>{fallback}</>;
      }
    } else if (requiredRole === 'TENANT_ADMIN') {
      if (!permissions.isTenantAdmin()) {
        return <>{fallback}</>;
      }
    } else if (user.role !== requiredRole) {
      return <>{fallback}</>;
    }
  }

  if (requiredRoles) {
    // Check if user has any of the required roles
    const hasRequiredRole = requiredRoles.some(role => {
      if (role === 'PLATFORM_ADMIN') {
        return permissions.isPlatformAdmin();
      }
      if (role === 'TENANT_ADMIN') {
        return permissions.isTenantAdmin();
      }
      return user.role === role;
    });
    
    if (!hasRequiredRole) {
      return <>{fallback}</>;
    }
  }

  // Check tenant-based access
  if (requireTenantMatch && allowedTenants && !allowedTenants.includes(user.tenantId)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Hook for checking permissions
export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();

  const hasRole = (role: string): boolean => {
    return isAuthenticated && user?.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return isAuthenticated && !!user?.role && roles.includes(user.role);
  };

  const canAccessTenant = (tenantId: string): boolean => {
    return isAuthenticated && user?.tenantId === tenantId;
  };

  const isPlatformAdmin = (): boolean => {
    return hasRole('PLATFORM_ADMIN') || hasRole('tmos_admin') || hasRole('Platform Administrator');
  };

  const isTenantAdmin = (): boolean => {
    return hasRole('TENANT_ADMIN') || hasRole('tenant_admin');
  };

  const canCreate = (): boolean => {
    return isPlatformAdmin() || isTenantAdmin();
  };

  const canUpdate = (): boolean => {
    return isPlatformAdmin() || isTenantAdmin();
  };

  const canDelete = (): boolean => {
    return isPlatformAdmin(); // Only platform admins can delete
  };

  const canView = (): boolean => {
    return isAuthenticated; // All authenticated users can view
  };

  return {
    hasRole,
    hasAnyRole,
    canAccessTenant,
    isPlatformAdmin,
    isTenantAdmin,
    canCreate,
    canUpdate,
    canDelete,
    canView,
    user,
    isAuthenticated
  };
};
